import { h, inject, Teleport } from 'vue'
import type { AlertID, AlertItem, AlertStore, AlertType } from './store'
import { ALERT_TYPES, useAlertStore } from './store'

import type {
  App,
  Component,
  FunctionalComponent,
  InjectionKey,
  MethodOptions,
} from 'vue'

export { ALERT_TYPES } from './store'
export type { AlertID, AlertItem, AlertType } from './store'

export const INJECTION_KEY: InjectionKey<AlertStore> = Symbol('alertKey')
export type Alert = {
  push(message: string, type: AlertType): AlertItem
  remove(id: AlertID): void
  clear(): void
  notice(message: string): void
  warning(message: string): void
  success(message: string): void
  error(message: string): void
}

const useSharedAlertStore = (): AlertStore => {
  const alert = inject(INJECTION_KEY)
  if (alert === undefined) {
    throw new Error('Alert is not installed')
  }
  return alert
}

export const createAlert = ({ clear, push, remove }: AlertStore): Alert => ({
  clear,
  remove,
  push,
  notice: (message: string) => push(message, ALERT_TYPES.NOTICE),
  warning: (message: string) => push(message, ALERT_TYPES.WARNING),
  success: (message: string) => push(message, ALERT_TYPES.SUCCESS),
  error: (message: string) => push(message, ALERT_TYPES.ERROR),
})
export const useAlert = (): Alert => createAlert(useSharedAlertStore())

interface AlertComponentProps {
  items: readonly AlertItem[]
}
type AlertComponentMethods = MethodOptions & { onClose(id: AlertID): void }

export const alertPortalize =
  <
    T extends Component<
      AlertComponentProps,
      any, // eslint-disable-line @typescript-eslint/no-explicit-any
      any, // eslint-disable-line @typescript-eslint/no-explicit-any
      any, // eslint-disable-line @typescript-eslint/no-explicit-any
      AlertComponentMethods
    >,
  >(
    ListComponent: T
  ): FunctionalComponent =>
  (_, { attrs, slots }) => {
    const { items, remove } = useSharedAlertStore()
    const component = h<AlertComponentProps>(
      ListComponent as any, // eslint-disable-line @typescript-eslint/no-explicit-any
      {
        ...attrs,
        items: items.value,
        onClose: remove,
      },
      slots.default?.()
    )

    return h(Teleport, { to: '#alert' }, [
      items.value.length > 0 ? component : null,
    ])
  }

const install = (() => {
  // eslint-disable-next-line fp/no-let
  let called = false
  return (app: App) => {
    if (called) {
      console.warn('AlertPlugin has been already installed')
      return
    }
    called = true
    const store = useAlertStore()
    app.provide(INJECTION_KEY, store)
  }
})()

export const alertPlugin = { install }
